// src/pages/AddStaffPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Table,
  Form,
  Input,
  Button,
  Select,
  Typography,
  Modal,
  Space,
  Row,
  Col,
  Alert,
  Switch,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatusTag,
  TabBar,
  tablePagination,
  useFlatPage,
} from '../components/ui';
import notify from "../utils/notify";

const { Text, Title } = Typography;
const { confirm } = Modal;

/* ---------------- Helpers ---------------- */

const isCanceled = (err) =>
  err?.code === 'ERR_CANCELED' ||
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* ---------------- Custom hook: debounce ---------------- */

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

/* ---------------- Custom hook: API calls with timeout + retry ----------------
   - Creates a NEW AbortController each attempt (so retry actually works).
   - Links an optional external signal so unmount/tab changes can cancel the axios call.
*/
const useApiWithTimeout = () => {
  return useCallback(async (requestFn, { timeoutMs = 15000, maxRetries = 2, signal } = {}) => {
    let attempt = 0;

    while (attempt <= maxRetries) {
      attempt += 1;

      const controller = new AbortController();
      let timeoutId = null;

      // Link external signal -> internal controller
      const abortHandler = () => controller.abort();
      if (signal) {
        if (signal.aborted) controller.abort();
        else signal.addEventListener('abort', abortHandler);
      }

      try {
        timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        const res = await requestFn(controller.signal);
        return res;
      } catch (err) {
        const externalAborted = !!signal?.aborted;
        const timedOutOrCanceled = controller.signal.aborted || isCanceled(err);

        // Retry only if we timed out/canceled AND the caller didn't explicitly abort
        if (!externalAborted && timedOutOrCanceled && attempt <= maxRetries) {
          await sleep(250 * attempt);
          continue;
        }
        throw err;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        if (signal) signal.removeEventListener('abort', abortHandler);
      }
    }
  }, []);
};

/* ---------------- Tab Components ---------------- */

const StaffListTab = React.memo(
  ({ staffList, loading, onEditClick, onActivateUser, onDeactivateUser, onDeleteStaff, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const filteredStaff = useMemo(() => {
      const q = debouncedSearch.trim().toLowerCase();
      return staffList.filter((staff) => {
        const full = `${staff.firstName ?? ''} ${staff.lastName ?? ''}`.trim().toLowerCase();
        const matchesSearch =
          !q ||
          full.includes(q) ||
          staff.email?.toLowerCase().includes(q) ||
          staff.roleName?.toLowerCase().includes(q);

        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && !!staff.isActive) ||
          (statusFilter === 'inactive' && !staff.isActive);

        return matchesSearch && matchesStatus;
      });
    }, [staffList, debouncedSearch, statusFilter]);

    const initials = (r) => `${(r.firstName || 'U')[0]}${(r.lastName || '')[0] || ''}`.toUpperCase();

    const columns = [
      {
        title: 'Staff',
        key: 'name',
        sorter: (a, b) => (a.firstName || '').localeCompare(b.firstName || ''),
        render: (_, r) => (
          <span className="hms-person">
            <span className="hms-avatar">{initials(r)}</span>
            <span className="hms-cell-stack">
              <span className="hms-cell-main">{`${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || 'Unnamed'}</span>
              <span className="hms-cell-sub">{r.email}</span>
            </span>
          </span>
        ),
      },
      {
        title: 'Role',
        dataIndex: 'roleName',
        key: 'role',
        width: 200,
        render: (roleName) => <StatusTag status="confirmed">{roleName || 'No role'}</StatusTag>,
        sorter: (a, b) => (a.roleName || '').localeCompare(b.roleName || ''),
      },
      {
        title: 'Status',
        dataIndex: 'isActive',
        key: 'status',
        width: 130,
        render: (isActive) => <StatusTag status={isActive ? 'available' : 'cancelled'}>{isActive ? 'Active' : 'Inactive'}</StatusTag>,
      },
      {
        title: '',
        key: 'actions',
        width: 140,
        align: 'right',
        render: (_, record) => (
          <RowActions
            actions={[
              { key: 'edit', label: 'Edit staff member', icon: 'edit', onClick: () => onEditClick(record) },
              record.isActive
                ? {
                    key: 'deactivate',
                    label: 'Deactivate user',
                    icon: 'close',
                    danger: true,
                    confirm: 'Deactivate this user? They will no longer be able to sign in.',
                    okText: 'Deactivate',
                    onClick: () => onDeactivateUser(record.userId),
                  }
                : { key: 'activate', label: 'Activate user', icon: 'accept', onClick: () => onActivateUser(record.userId) },
              {
                key: 'delete',
                label: 'Delete user',
                icon: 'delete',
                danger: true,
                confirm: 'Delete this user?',
                okText: 'Delete',
                onClick: () => onDeleteStaff(record.userId),
              },
            ]}
          />
        ),
      },
    ];

    return (
      <SectionCard
        flush
        title={<CardTitle count={staffList.length}>Staff</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search name, email or role' }}
            onRefresh={onRefresh}
            loading={loading}
          >
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 140 }}
              options={[
                { label: 'All status', value: 'all' },
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
              ]}
            />
          </ListToolbar>
        }
      >
        <Table
          className="hms-table"
          columns={columns}
          dataSource={filteredStaff}
          rowKey="userId"
          loading={loading}
          pagination={tablePagination('staff member')}
          scroll={{ x: 800 }}
          locale={{ emptyText: <div className="hms-empty">No staff match this view</div> }}
        />
      </SectionCard>
    );
  }
);

const RolesTab = React.memo(({ availableRoles, loading, onShowRoleModal, onRefresh }) => {
  const columns = [
    { title: 'Role', dataIndex: 'name', key: 'name', render: (name) => <span className="hms-cell-main">{name}</span> },
    { title: 'ID', dataIndex: 'id', key: 'id', width: 320, render: (id) => <span className="hms-muted">{id}</span> },
  ];

  return (
    <SectionCard
      flush
      title={<CardTitle count={availableRoles.length}>Roles</CardTitle>}
      extra={
        <ListToolbar onRefresh={onRefresh} loading={loading}>
          <Button type="primary" onClick={onShowRoleModal}>
            Create role
          </Button>
        </ListToolbar>
      }
    >
      <Table
        className="hms-table"
        columns={columns}
        dataSource={availableRoles}
        rowKey="id"
        loading={loading}
        pagination={tablePagination('role', { hideOnSinglePage: true })}
        locale={{ emptyText: <div className="hms-empty">No roles yet. Create your first role.</div> }}
      />
    </SectionCard>
  );
});

/* ---------------- Main Component ---------------- */

const AddStaffPage = () => {
  useFlatPage();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  const [activeTab, setActiveTab] = useState('1');

  const [availableRoles, setAvailableRoles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState({ staff: false, roles: false, general: false });

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const [currentStaff, setCurrentStaff] = useState(null);

  const [formError, setFormError] = useState(null);
  const [roleFormError, setRoleFormError] = useState(null);

  const navigate = useNavigate();
  const { authData } = useAuth();
  const apiWithTimeout = useApiWithTimeout();

  const abortControllers = useRef(new Set());
  const loadedTabsRef = useRef(new Set(['1']));
  const rolesLoadedRef = useRef(false);
  const rolesFetchInFlightRef = useRef(false); // prevents double fetch on first open

  const createAbortController = useCallback(() => {
    const controller = new AbortController();
    abortControllers.current.add(controller);
    return controller;
  }, []);

  const cleanupAbortController = useCallback((controller) => {
    abortControllers.current.delete(controller);
  }, []);

  const runAbortable = useCallback(
    (fn) => {
      const controller = createAbortController();
      Promise.resolve(fn(controller.signal)).finally(() => cleanupAbortController(controller));
      return controller;
    },
    [createAbortController, cleanupAbortController]
  );

  useEffect(() => {
    return () => {
      abortControllers.current.forEach((c) => c.abort());
      // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup must abort the controllers that exist at unmount, so it reads the ref then
      abortControllers.current.clear();
    };
  }, []);

  /* ---------- Data Fetching ---------- */

  const fetchRoles = useCallback(
    async (signal) => {
      if (!authData?.hotelId) return;

      setLoading((prev) => ({ ...prev, roles: true }));
      try {
        const res = await apiWithTimeout(
          (abortSignal) =>
            axiosInstance.get(`/roles`, {
              params: { hotelId: authData.hotelId },
              signal: abortSignal,
            }),
          { signal }
        );

        const list = Array.isArray(res.data) ? res.data : [];
        const normalized = list
          .map((r) => ({
            id: r?.id ?? r?.roleId ?? r?.Id ?? r?.ID ?? null,
            name: r?.name ?? r?.roleName ?? r?.Name ?? r?.RoleName ?? 'Role',
          }))
          .filter((r) => r.id && r.name);

        setAvailableRoles(normalized);
        rolesLoadedRef.current = true;
      } catch (error) {
        if (isCanceled(error) || signal?.aborted) return;

        console.error('Failed to load roles:', error);
        notify.error({
          message: 'Error Loading Roles',
          description: error?.response?.data?.message || 'Failed to load roles. Please try again.',
        });
      } finally {
        setLoading((prev) => ({ ...prev, roles: false }));
      }
    },
    [authData?.hotelId, apiWithTimeout]
  );

  const fetchStaff = useCallback(
    async (signal) => {
      if (!authData?.hotelId) return;

      setLoading((prev) => ({ ...prev, staff: true }));
      try {
        const res = await apiWithTimeout(
          (abortSignal) =>
            axiosInstance.get(`/User`, {
              params: { hotelId: authData.hotelId },
              signal: abortSignal,
            }),
          { signal }
        );

        const raw = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
        const mapped = raw.map((s) => {
          const userId = s.userId ?? s.id ?? s.UserId ?? s.Id;
          const roleId = s?.roleId ?? s?.RoleId ?? s?.Role?.Id ?? s?.role?.Id ?? null;
          const roleName = s?.roleName ?? s?.RoleName ?? s?.Role?.Name ?? s?.role?.Name ?? s?.role?.name ?? 'No Role';
          return { ...s, userId, roleId, roleName };
        });

        setStaffList(mapped);
      } catch (error) {
        if (isCanceled(error) || signal?.aborted) return;

        console.error('Failed to load staff:', error);
        notify.error({
          message: 'Error Loading Staff',
          description: error?.response?.data?.message || 'Failed to load staff. Please try again.',
        });
      } finally {
        setLoading((prev) => ({ ...prev, staff: false }));
      }
    },
    [authData?.hotelId, apiWithTimeout]
  );

  // Initial load (staff list)
  useEffect(() => {
    const controller = createAbortController();
    fetchStaff(controller.signal).finally(() => cleanupAbortController(controller));
    return () => {
      controller.abort();
      cleanupAbortController(controller);
    };
  }, [createAbortController, cleanupAbortController, fetchStaff]);

  // Ensure roles are available when opening Add/Edit staff (not only when roles tab is visited)
  const ensureRolesLoaded = useCallback(() => {
    if (!authData?.hotelId) return;
    if (rolesLoadedRef.current) return;
    if (rolesFetchInFlightRef.current) return;

    rolesFetchInFlightRef.current = true;

    const controller = createAbortController();
    fetchRoles(controller.signal)
      .finally(() => {
        rolesFetchInFlightRef.current = false;
        cleanupAbortController(controller);
      });
  }, [authData?.hotelId, createAbortController, cleanupAbortController, fetchRoles]);

  useEffect(() => {
    if (showStaffModal || showEditModal) ensureRolesLoaded();
  }, [showStaffModal, showEditModal, ensureRolesLoaded]);

  /* ---------- Tab handling ---------- */

  const handleTabChange = useCallback(
    (key) => {
      setActiveTab(key);

      if (!loadedTabsRef.current.has(key)) {
        loadedTabsRef.current.add(key);

        if (key === '2') {
          runAbortable(fetchRoles);
        }
      }
    },
    [fetchRoles, runAbortable]
  );

  /* ---------- Staff Management ---------- */

  const handleStaffSubmit = async (values) => {
    const controller = createAbortController();

    try {
      setLoading((prev) => ({ ...prev, general: true }));
      setFormError(null);

      const payload = {
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        email: values.email?.trim(),
        userName: values.email?.trim(),
        password: values.password,
        roleId: values.role,
        hotelId: authData.hotelId,
        isActive: true,
      };

      const res = await apiWithTimeout(
        (abortSignal) => axiosInstance.post('/Staff', payload, { signal: abortSignal }),
        { signal: controller.signal }
      );

      if (res.status === 201) {
        notify.success({ message: 'Success', description: 'Staff added successfully!' });
        form.resetFields();
        setShowStaffModal(false);
        await fetchStaff(controller.signal);
      }
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;

      const status = error?.response?.status;
      const data = error?.response?.data ?? {};
      const msg = data.message || data.title || 'Something went wrong';
      const errors = data.errors || null;

      if (status === 409) {
        form.setFields([{ name: 'email', errors: [msg || 'Email already exists.'] }]);
        notify.warning({ message: 'Duplicate email', description: msg || 'Email already exists.' });
        return;
      }

      if (status === 400 && errors && typeof errors === 'object') {
        const mapServerField = (k = '') => k.replace(/^User\./, '').replace(/^EmailAddress$/i, 'email');
        const fields = Object.entries(errors).map(([key, val]) => ({
          name: mapServerField(key),
          errors: [Array.isArray(val) ? val[0] ?? 'Invalid value' : String(val)],
        }));
        form.setFields(fields);
        notify.error({ message: 'Validation error', description: 'Please fix the highlighted fields.' });
        return;
      }

      setFormError(msg || 'Failed to add staff');
      notify.error({ message: 'Error', description: msg || 'Failed to add staff' });
    } finally {
      setLoading((prev) => ({ ...prev, general: false }));
      cleanupAbortController(controller);
    }
  };

  const handleEditSubmit = async (values) => {
    if (!currentStaff?.userId) return;

    const controller = createAbortController();

    try {
      setLoading((prev) => ({ ...prev, general: true }));
      setFormError(null);

      const payload = {
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        roleId: values.role,
        isActive: values.isActive,
      };

      const res = await apiWithTimeout(
        (abortSignal) => axiosInstance.put(`/User/User/${currentStaff.userId}`, payload, { signal: abortSignal }),
        { signal: controller.signal }
      );

      if (res.status === 200) {
        notify.success({ message: 'Success', description: 'Staff updated successfully!' });
        editForm.resetFields();
        setShowEditModal(false);
        await fetchStaff(controller.signal);
      }
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;

      const msg = error?.response?.data?.message || 'Failed to update staff';
      setFormError(msg);
      notify.error({ message: 'Error', description: msg });
    } finally {
      setLoading((prev) => ({ ...prev, general: false }));
      cleanupAbortController(controller);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    const controller = createAbortController();

    try {
      setLoading((prev) => ({ ...prev, general: true }));
      const endpoint = currentStatus ? 'deactivate' : 'activate';

      const res = await apiWithTimeout(
        (abortSignal) => axiosInstance.put(`/User/${endpoint}/${userId}`, {}, { signal: abortSignal }),
        { signal: controller.signal }
      );

      notify.success({ message: 'Success', description: res?.data?.message ?? 'Updated' });
      setStaffList((prev) => prev.map((s) => (s.userId === userId ? { ...s, isActive: !currentStatus } : s)));
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;

      const msg = error?.response?.data?.message || 'Failed to update user status';
      notify.error({ message: 'Error', description: msg });
    } finally {
      setLoading((prev) => ({ ...prev, general: false }));
      cleanupAbortController(controller);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler only uses stable setters and refs; stabilising it is a larger refactor
  const handleActivateUser = async (userId) => toggleUserStatus(userId, false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler only uses stable setters and refs; stabilising it is a larger refactor
  const handleDeactivateUser = async (userId) => {
    confirm({
      title: 'Deactivate User?',
      content: 'This will prevent the user from accessing the system.',
      okText: 'Deactivate',
      okType: 'danger',
      async onOk() {
        await toggleUserStatus(userId, true);
      },
    });
  };

  const handleRoleSubmit = async (values) => {
    const controller = createAbortController();

    try {
      setLoading((prev) => ({ ...prev, general: true }));
      setRoleFormError(null);

      const payload = {
        name: values.roleName?.trim(),
        description: values.description?.trim(),
        hotelId: authData.hotelId,
      };

      const res = await apiWithTimeout(
        (abortSignal) => axiosInstance.post('/roles', payload, { signal: abortSignal }),
        { signal: controller.signal }
      );

      if (res.status === 201) {
        notify.success({ message: 'Success', description: 'Role created successfully!' });
        roleForm.resetFields();
        setShowRoleModal(false);
        rolesLoadedRef.current = false; // force reload so new role appears
        await fetchRoles(controller.signal);
      }
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;

      const msg = error?.response?.data?.message || 'Failed to create role';
      setRoleFormError(msg);
      notify.error({ message: 'Error', description: msg });
    } finally {
      setLoading((prev) => ({ ...prev, general: false }));
      cleanupAbortController(controller);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler only uses stable setters and refs; stabilising it is a larger refactor
  const deleteStaff = async (userId) => {
    confirm({
      title: 'Delete Staff Member?',
      content: 'Are you sure you want to delete this staff member?',
      async onOk() {
        const controller = createAbortController();

        try {
          await apiWithTimeout(
            (abortSignal) => axiosInstance.delete(`/User/${userId}`, { signal: abortSignal }),
            { signal: controller.signal }
          );
          notify.success({ message: 'Success', description: 'Staff deleted' });
          await fetchStaff(controller.signal);
        } catch (error) {
          if (isCanceled(error) || controller.signal.aborted) return;
          notify.error({ message: 'Error', description: 'Failed to delete staff' });
        } finally {
          cleanupAbortController(controller);
        }
      },
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler only uses stable setters and refs; stabilising it is a larger refactor
  const handleEditClick = (staff) => {
    setCurrentStaff(staff);
    editForm.setFieldsValue({
      firstName: staff.firstName,
      lastName: staff.lastName,
      email: staff.email,
      role: staff.roleId,
      isActive: staff.isActive,
    });
    setShowEditModal(true);
  };

  const refreshCurrentTab = useCallback(() => {
    if (activeTab === '1') return runAbortable(fetchStaff);
    if (activeTab === '2') return runAbortable(fetchRoles);
    return null;
  }, [activeTab, fetchRoles, fetchStaff, runAbortable]);

  const currentBadgeCount = activeTab === '1' ? staffList.length : activeTab === '2' ? availableRoles.length : 0;
  const currentTabLoading = activeTab === '1' ? loading.staff : activeTab === '2' ? loading.roles : false;

  /* ---------- Tabs v6 items API ---------- */

  const tabsItems = useMemo(
    () => [
      {
        key: '1',
        label: 'Staff',
        count: staffList.length,
        children: (
          <StaffListTab
            staffList={staffList}
            loading={loading.staff}
            onEditClick={handleEditClick}
            onActivateUser={handleActivateUser}
            onDeactivateUser={handleDeactivateUser}
            onDeleteStaff={deleteStaff}
            onRefresh={() => runAbortable(fetchStaff)}
          />
        ),
      },
      {
        key: '2',
        label: 'Roles',
        count: availableRoles.length,
        children: (
          <RolesTab
            availableRoles={availableRoles}
            loading={loading.roles}
            onShowRoleModal={() => setShowRoleModal(true)}
            onRefresh={() => runAbortable(fetchRoles)}
          />
        ),
      },
    ],
    [
      availableRoles,
      deleteStaff,
      fetchRoles,
      fetchStaff,
      handleActivateUser,
      handleDeactivateUser,
      handleEditClick,
      loading.roles,
      loading.staff,
      runAbortable,
      staffList,
    ]
  );

  const currentTabItem = tabsItems.find((i) => i.key === activeTab) || tabsItems[0];

  return (
    <div className="hms-page sm-page">

      <PageHeader
        actions={
          <>
            <Button onClick={() => setShowInfoModal(true)}>Guide</Button>
            {/* IMPORTANT: Only open modal here (roles are loaded via effect) */}
            <Button type="primary" onClick={() => setShowStaffModal(true)}>
              Add staff
            </Button>
          </>
        }
      />

      <SectionCard flush>
        <TabBar
          tabs={tabsItems.map(({ key, label, count }) => ({ key, label, count }))}
          value={currentTabItem.key}
          onChange={handleTabChange}
        />
      </SectionCard>

      <div style={{ marginTop: 16 }}>{currentTabItem.children}</div>

      {/* ---------------- Modals (kept inline to avoid remount surprises) ---------------- */}

      {/* Add Staff Modal */}
      <Modal
        title="Add staff member"
        open={showStaffModal}
        onCancel={() => {
          if (form.isFieldsTouched()) {
            confirm({
              title: 'Discard Changes?',
              content: 'You have unsaved changes. Are you sure you want to close?',
              onOk() {
                form.resetFields();
                setShowStaffModal(false);
                setFormError(null);
              },
            });
          } else {
            setShowStaffModal(false);
            setFormError(null);
          }
        }}
        footer={null}
        width={700}
        afterClose={() => {
          form.resetFields();
          setFormError(null);
        }}
      >
        {formError && (
          <Alert
            title="Error"
            description={formError}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 24 }}
          />
        )}

        <Form form={form} layout="vertical" onFinish={handleStaffSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[
                  { required: true, message: 'Please enter first name' },
                  { min: 2, message: 'Minimum 2 characters' },
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[
                  { required: true, message: 'Please enter last name' },
                  { min: 2, message: 'Minimum 2 characters' },
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: 'Please enter password' },
                  { min: 6, message: 'Minimum 6 characters' },
                ]}
              >
                <Input.Password placeholder="Create password" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm password" />
              </Form.Item>
            </Col>
          </Row>

          {availableRoles.length === 0 && !loading.roles && (
            <Alert
              type="warning"
              title="No roles available"
              description="Please create roles before adding staff members."
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item label="Role" name="role" rules={[{ required: true, message: 'Please select a role' }]}>
            <Select
              placeholder={loading.roles ? 'Loading roles...' : 'Select role'}
              loading={loading.roles}
              disabled={loading.roles}
              options={availableRoles.map((r) => ({ label: r.name, value: r.id }))}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                onClick={() => {
                  if (form.isFieldsTouched()) {
                    confirm({
                      title: 'Discard Changes?',
                      content: 'You have unsaved changes. Are you sure you want to cancel?',
                      onOk() {
                        form.resetFields();
                        setShowStaffModal(false);
                        setFormError(null);
                      },
                    });
                  } else {
                    setShowStaffModal(false);
                    setFormError(null);
                  }
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading.general} disabled={availableRoles.length === 0}>
                Add Staff Member
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Staff Modal */}
      <Modal
        title="Edit staff member"
        open={showEditModal}
        onCancel={() => {
          if (editForm.isFieldsTouched()) {
            confirm({
              title: 'Discard Changes?',
              content: 'You have unsaved changes. Are you sure you want to close?',
              onOk() {
                editForm.resetFields();
                setShowEditModal(false);
                setFormError(null);
              },
            });
          } else {
            setShowEditModal(false);
            setFormError(null);
          }
        }}
        footer={null}
        width={700}
        afterClose={() => {
          editForm.resetFields();
          setFormError(null);
        }}
      >
        {formError && (
          <Alert
            title="Error"
            description={formError}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 24 }}
          />
        )}

        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[
                  { required: true, message: 'Please enter first name' },
                  { min: 2, message: 'Minimum 2 characters' },
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[
                  { required: true, message: 'Please enter last name' },
                  { min: 2, message: 'Minimum 2 characters' },
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Email">
            <Input value={currentStaff?.email} readOnly disabled />
          </Form.Item>

          <Form.Item label="Role" name="role" rules={[{ required: true, message: 'Please select a role' }]}>
            <Select
              placeholder="Select role"
              loading={loading.roles}
              disabled={loading.roles}
              options={availableRoles.map((r) => ({ label: r.name, value: r.id }))}
            />
          </Form.Item>

          <Form.Item label="Account Status" name="isActive" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                onClick={() => {
                  if (editForm.isFieldsTouched()) {
                    confirm({
                      title: 'Discard Changes?',
                      content: 'You have unsaved changes. Are you sure you want to cancel?',
                      onOk() {
                        editForm.resetFields();
                        setShowEditModal(false);
                        setFormError(null);
                      },
                    });
                  } else {
                    setShowEditModal(false);
                    setFormError(null);
                  }
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading.general} disabled={availableRoles.length === 0}>
                Update Staff Member
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Role Modal */}
      <Modal
        title="Create role"
        open={showRoleModal}
        onCancel={() => {
          if (roleForm.isFieldsTouched()) {
            confirm({
              title: 'Discard Changes?',
              content: 'You have unsaved changes. Are you sure you want to close?',
              onOk() {
                roleForm.resetFields();
                setShowRoleModal(false);
                setRoleFormError(null);
              },
            });
          } else {
            setShowRoleModal(false);
            setRoleFormError(null);
          }
        }}
        footer={null}
        width={500}
        afterClose={() => {
          roleForm.resetFields();
          setRoleFormError(null);
        }}
      >
        {roleFormError && (
          <Alert
            title="Error"
            description={roleFormError}
            type="error"
            showIcon
            closable
            onClose={() => setRoleFormError(null)}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form form={roleForm} layout="vertical" onFinish={handleRoleSubmit}>
          <Form.Item
            name="roleName"
            label="Role Name"
            rules={[
              { required: true, message: 'Please enter role name' },
              { min: 3, message: 'Minimum 3 characters' },
              { max: 100, message: 'Maximum 100 characters' },
            ]}
          >
            <Input placeholder="Enter role name (e.g., Manager, Receptionist)" maxLength={100} showCount />
          </Form.Item>

          <Form.Item name="description" label="Description (Optional)" rules={[{ max: 500, message: 'Maximum 500 characters' }]}>
            <Input.TextArea placeholder="Enter role description" rows={3} maxLength={500} showCount />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                onClick={() => {
                  if (roleForm.isFieldsTouched()) {
                    confirm({
                      title: 'Discard Changes?',
                      content: 'You have unsaved changes. Are you sure you want to cancel?',
                      onOk() {
                        roleForm.resetFields();
                        setShowRoleModal(false);
                        setRoleFormError(null);
                      },
                    });
                  } else {
                    setShowRoleModal(false);
                    setRoleFormError(null);
                  }
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading.general}>
                Create Role
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Info Modal */}
      <Modal
        title="Staff management guide"
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowInfoModal(false)} type="primary">
            Got it
          </Button>,
        ]}
        width={600}
      >
        <p className="hms-modal-note">Manage your hotel staff and their roles. Create roles first, then give people those roles.</p>
        <Section title="What you can do">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Create roles with specific permissions</li>
            <li>Add staff members with an email and password</li>
            <li>Search and filter staff by name, role or status</li>
            <li>Activate or deactivate accounts</li>
          </ul>
        </Section>
        <Section title="Right now">
          <DetailList
            items={[
              { label: 'Staff', value: staffList.length },
              { label: 'Active staff', value: staffList.filter((s) => s.isActive).length },
              { label: 'Roles', value: availableRoles.length },
            ]}
          />
        </Section>
      </Modal>
    </div>
  );
};

export default AddStaffPage;
